import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { hvFaqs } from '../data/habitationVelier'

export function useStructuredData() {
  const route = useRoute()

  // Base organization data
  const organizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "83 Islands Distillery",
    "url": "https://83islands.com",
    "logo": "https://83islands.com/image/83IslandsDistillery.svg",
    "description": "83 Islands Distillery crafts authentic Vanuatu rum from locally grown sugarcane. Experience world-class rum making in the heart of Port Vila with guided tours and tastings.",
    "foundingDate": "2020",
    "founder": {
      "@type": "Person",
      "name": "83 Islands Distillery Founders"
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Port Vila",
      "addressLocality": "Port Vila",
      "addressRegion": "Shefa Province",
      "postalCode": "PO1276",
      "addressCountry": "VU"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+678-755-0083",
      "contactType": "customer service",
      "availableLanguage": ["English", "French", "Bislama"]
    },
    "sameAs": [
      "https://www.facebook.com/83islands",
      "https://www.instagram.com/83islands"
    ],
    "areaServed": {
      "@type": "Country",
      "name": "Vanuatu"
    }
  }

  // Local business data
  const localBusinessData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://83islands.com/#business",
    "name": "83 Islands Distillery",
    "image": [
      "https://83islands.com/image/83islands_location.jpg",
      "https://83islands.com/image/83islands_shop.jpg",
      "https://83islands.com/image/productionfloor.jpg"
    ],
    "description": "Vanuatu's premier rum distillery offering guided tours, tastings, and authentic island spirits. Experience the craft of rum making in Port Vila.",
    "url": "https://83islands.com",
    "telephone": "+678-755-0085",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Port Vila",
      "addressLocality": "Port Vila",
      "addressRegion": "Shefa Province",
      "postalCode": "PO1276",
      "addressCountry": "VU"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "-17.750208",
      "longitude": "168.367278"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "17:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "12:00",
        "closes": "18:00"
      }
    ],
    "priceRange": "$$",
    "paymentAccepted": ["Cash", "Credit Card", "Visa", "Mastercard"],
    "currenciesAccepted": "VUV",
    "hasMap": "https://maps.google.com/?q=Port+Vila+Vanuatu",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "150",
      "bestRating": "5",
      "worstRating": "1"
    },
    "amenityFeature": [
      {
        "@type": "LocationFeatureSpecification",
        "name": "Distillery Tours",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification", 
        "name": "Tasting Room",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Gift Shop",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Parking",
        "value": true
      }
    ]
  }

  // Tour events data
  const tourEventsData = computed(() => {
    const baseUrl = 'https://83islands.com'
    const currentDate = new Date()
    
    return {
      "@context": "https://schema.org",
      "@type": "Event",
      "name": "83 Islands Distillery Tour & Tasting",
      "description": "Experience the art of rum making at 83 Islands Distillery. Guided tour through our distillery process followed by a curated tasting of our premium rums.",
      "url": `${baseUrl}/tours`,
      "image": [
        `${baseUrl}/image/83islands_location.jpg`,
        `${baseUrl}/image/productionfloor.jpg`,
        `${baseUrl}/image/rackhouse.jpg`
      ],
      "startDate": currentDate.toISOString().split('T')[0],
      "endDate": new Date(currentDate.getTime() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1 year from now
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      "location": {
        "@type": "Place",
        "name": "83 Islands Distillery",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Port Vila",
          "addressLocality": "Port Vila",
          "addressRegion": "Shefa Province",
          "postalCode": "PO1276",
          "addressCountry": "VU"
        }
      },
      "organizer": {
        "@type": "Organization",
        "name": "83 Islands Distillery",
        "url": baseUrl
      },
      "offers": [
        {
          "@type": "Offer",
          "name": "Distillery Experience Tour",
          "description": "Guided journey through our artisanal rum-making process",
          "price": "4000",
          "priceCurrency": "VUV",
          "availability": "https://schema.org/InStock",
          "validFrom": currentDate.toISOString().split('T')[0],
          "url": `${baseUrl}/tours`
        },
        {
          "@type": "Offer", 
          "name": "Cellar Door Tasting",
          "description": "Curated tasting of our premium rums",
          "price": "2500",
          "priceCurrency": "VUV",
          "availability": "https://schema.org/InStock",
          "validFrom": currentDate.toISOString().split('T')[0],
          "url": `${baseUrl}/tours`
        }
      ],
      "maximumAttendeeCapacity": 16,
      "remainingAttendeeCapacity": 16
    }
  })

  // 83 Islands Nights events
  const islandsNightsEvents = computed(() => {
    const baseUrl = 'https://83islands.com'
    const eventDates = [
      '2025-01-31', '2025-02-28', '2025-03-28', '2025-04-25', '2025-05-30',
      '2025-06-27', '2025-07-25', '2025-08-29', '2025-09-26', '2025-10-31', '2025-11-28'
    ]

    return eventDates.map(date => ({
      "@context": "https://schema.org",
      "@type": "Event",
      "name": "83 Islands Night",
      "description": "Monthly celebration featuring live music, special rum tastings, and island culture at 83 Islands Distillery. One Night, 83 Stories.",
      "url": `${baseUrl}/tours`,
      "image": `${baseUrl}/image/83islands_shop.jpg`,
      "startDate": `${date}T18:00:00+11:00`,
      "endDate": `${date}T22:00:00+11:00`,
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      "performer": {
        "@type": "MusicGroup",
        "name": "Musical Guest TBD",
        "description": "Live musical performance featuring local and international artists"
      },
      "location": {
        "@type": "Place",
        "name": "83 Islands Distillery",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Port Vila",
          "addressLocality": "Port Vila", 
          "addressRegion": "Shefa Province",
          "postalCode": "PO1276",
          "addressCountry": "VU"
        }
      },
      "organizer": {
        "@type": "Organization",
        "name": "83 Islands Distillery",
        "url": baseUrl
      },
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "VUV",
        "availability": "https://schema.org/InStock",
        "url": `${baseUrl}/tours`
      }
    }))
  })

  // Product data for rum products
  const productData = computed(() => {
    const baseUrl = 'https://83islands.com'
    
    return [
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "83 Islands Pure Sugarcane Rum",
        "description": "Authentic Vanuatu rum crafted from locally grown sugarcane, aged in tropical climate for rich, complex flavors.",
        "image": `${baseUrl}/image/83islands_location.jpg`,
        "brand": {
          "@type": "Brand",
          "name": "83 Islands Distillery"
        },
        "manufacturer": {
          "@type": "Organization",
          "name": "83 Islands Distillery",
          "url": baseUrl
        },
        "category": "Spirits > Rum",
        "alcoholContent": "40%",
        "volume": "750ml",
        "countryOfOrigin": "Vanuatu",
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "VUV",
          "seller": {
            "@type": "Organization",
            "name": "83 Islands Distillery"
          }
        }
      }
    ]
  })

  // Entropy Index structured data: the page is the canonical reference for the
  // 83 Islands Entropy Index framework. Emitting both a TechArticle (so the page
  // itself is citable) and four DefinedTerm entries (so AI can attribute the
  // named framework to 83 Islands as the originator).
  const entropyIndexData = computed(() => {
    const baseUrl = 'https://83islands.com'
    const pageUrl = `${baseUrl}/entropy-index`

    const article = {
      "@context": "https://schema.org",
      "@type": "TechArticle",
      "@id": `${pageUrl}#article`,
      "headline": "83 Islands Entropy Index™ — Measuring Nature's Effect on Rum Maturation",
      "description": "A proprietary framework, authored by 83 Islands Distillery, that quantifies how atmospheric pressure changes, cyclone winds, and seismic activity shape rum during maturation in Vanuatu. Comprises three trademarked sub-indices: Pressure Flux Index, Wind Agitation Index, and Seismic Energy Index.",
      "image": [
        `${baseUrl}/image/83islands_yasurbw.jpg`,
        `${baseUrl}/image/rackhouse.jpg`
      ],
      "author": {
        "@type": "Organization",
        "name": "83 Islands Distillery",
        "url": baseUrl
      },
      "publisher": {
        "@type": "Organization",
        "name": "83 Islands Distillery",
        "url": baseUrl,
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/image/83IslandsDistillery.svg`
        }
      },
      "datePublished": "2025-10-15",
      "dateModified": new Date().toISOString().split('T')[0],
      "inLanguage": "en",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": pageUrl
      },
      "keywords": "Entropy Index, rum maturation, tropical aging, Vanuatu rum, Pressure Flux Index, Wind Agitation Index, Seismic Energy Index, cyclone effects on spirits, seismic effects on aging, 83 Islands Distillery"
    }

    const termSet = {
      "@context": "https://schema.org",
      "@type": "DefinedTermSet",
      "@id": `${pageUrl}#entropy-index-framework`,
      "name": "83 Islands Entropy Index Framework",
      "description": "A framework developed by 83 Islands Distillery in Vanuatu that quantifies how natural forces — atmospheric pressure swings, cyclone winds, and seismic activity along the Pacific Ring of Fire — affect rum maturation. Composed of three sub-indices.",
      "url": pageUrl,
      "creator": {
        "@type": "Organization",
        "name": "83 Islands Distillery",
        "url": baseUrl
      }
    }

    const entropyIndexTerm = {
      "@context": "https://schema.org",
      "@type": "DefinedTerm",
      "@id": `${pageUrl}#entropy-index`,
      "name": "83 Islands Entropy Index",
      "alternateName": ["Entropy Index", "ENTROPY INDEX"],
      "description": "The 83 Islands Entropy Index is a proprietary framework, developed and trademarked by 83 Islands Distillery, that quantifies how natural forces — atmospheric pressure swings, cyclone winds, and seismic activity — affect rum during maturation in Vanuatu's tropical climate. It is the first published methodology of its kind in the spirits industry. The framework comprises three sub-indices: Pressure Flux Index, Wind Agitation Index, and Seismic Energy Index, each derived from real meteorological and seismic data measured at the distillery's location in Port Vila.",
      "inDefinedTermSet": `${pageUrl}#entropy-index-framework`,
      "url": pageUrl
    }

    const pfiTerm = {
      "@context": "https://schema.org",
      "@type": "DefinedTerm",
      "@id": `${pageUrl}#pressure-flux-index`,
      "name": "Pressure Flux Index",
      "alternateName": ["PFI"],
      "description": "A sub-index of the 83 Islands Entropy Index that captures atmospheric pressure swings — particularly during cyclone approaches, when sea-level pressure can fall from ~1013 millibars to ~935 millibars. These swings cause maturation casks to expand and contract, drawing rum deeper into oak and back out, accelerating the exchange of vanillin, tannin, and sweetness.",
      "inDefinedTermSet": `${pageUrl}#entropy-index-framework`,
      "url": `${pageUrl}#pressure-flux-index`
    }

    const waiTerm = {
      "@context": "https://schema.org",
      "@type": "DefinedTerm",
      "@id": `${pageUrl}#wind-agitation-index`,
      "name": "Wind Agitation Index",
      "alternateName": ["WAI"],
      "description": "A sub-index of the 83 Islands Entropy Index that quantifies mechanical vibration of the rackhouse and casks under sustained cyclone winds, derived from real wind data. Wind agitation enhances spirit-oak contact and oxygen ingress, contributing to oxidation and structural development that stillness cannot produce.",
      "inDefinedTermSet": `${pageUrl}#entropy-index-framework`,
      "url": `${pageUrl}#wind-agitation-index`
    }

    const seiTerm = {
      "@context": "https://schema.org",
      "@type": "DefinedTerm",
      "@id": `${pageUrl}#seismic-energy-index`,
      "name": "Seismic Energy Index",
      "alternateName": ["SEI"],
      "description": "A sub-index of the 83 Islands Entropy Index that records the cumulative energy released by earthquakes near the distillery, derived from real seismic readings. Uses the seismologists' formula Energy = 10^(1.5 × Magnitude) joules. Vanuatu sits on the Pacific Ring of Fire and experiences frequent tremors that impart steady, low-amplitude motion to maturing casks.",
      "inDefinedTermSet": `${pageUrl}#entropy-index-framework`,
      "url": `${pageUrl}#seismic-energy-index`
    }

    return [article, termSet, entropyIndexTerm, pfiTerm, waiTerm, seiTerm]
  })

  // Breadcrumb data
  // Habitation Velier 83 Islands 2021: FAQ only. No Product markup: we don't sell it, and Google rejects a Product without offers, review or rating
  const habitationVelierData = computed(() => {
    return [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": hvFaqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      }
    ]
  })

  const breadcrumbData = computed(() => {
    const baseUrl = 'https://83islands.com'
    const pathSegments = route.path.split('/').filter(segment => segment)
    
    const breadcrumbItems = [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      }
    ]

    let currentPath = baseUrl
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`
      breadcrumbItems.push({
        "@type": "ListItem",
        "position": index + 2,
        "name": segment.charAt(0).toUpperCase() + segment.slice(1).replace('-', ' '),
        "item": currentPath
      })
    })

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbItems
    }
  })

  // Get structured data based on current route
  const getStructuredData = computed(() => {
    const structuredData = []

    // Always include organization and local business data
    structuredData.push(organizationData)
    structuredData.push(localBusinessData)

    // Add route-specific data
    switch (route.name) {
      case 'Tours':
        structuredData.push(tourEventsData.value)
        structuredData.push(...upcomingSpecialEvents.value)
        break
      case 'Spirits':
        structuredData.push(...productData.value)
        break
      case 'Discover':
      case 'Intro':
        structuredData.push(...islandsNightsEvents.value)
        structuredData.push(...upcomingSpecialEvents.value)
        break
      case 'Entropy Index':
        structuredData.push(...entropyIndexData.value)
        break
      case 'Habitation Velier':
        structuredData.push(...habitationVelierData.value)
        break
      case 'Article':
        // Article structured data is handled in Article.vue component
        break
    }

    // Always include breadcrumbs
    structuredData.push(breadcrumbData.value)

    return structuredData
  })

  // Function to create event structured data dynamically
  const createEventStructuredData = (eventData) => {
    const baseUrl = 'https://83islands.com'
    
    return {
      "@context": "https://schema.org",
      "@type": "Event",
      "name": eventData.name || "83 Islands Night",
      "description": eventData.description || "Monthly celebration featuring live music, special rum tastings, and island culture at 83 Islands Distillery. One Night, 83 Stories.",
      "url": eventData.url || `${baseUrl}/tours`,
      "image": eventData.image || `${baseUrl}/image/83islands_shop.jpg`,
      "startDate": eventData.startDate,
      "endDate": eventData.endDate,
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      "performer": {
        "@type": "MusicGroup",
        "name": eventData.performer || "Musical Guest TBD",
        "description": eventData.performerDescription || "Live musical performance featuring local and international artists"
      },
      "location": {
        "@type": "Place",
        "name": "83 Islands Distillery",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Port Vila",
          "addressLocality": "Port Vila", 
          "addressRegion": "Shefa Province",
          "postalCode": "PO1276",
          "addressCountry": "VU"
        }
      },
      "organizer": {
        "@type": "Organization",
        "name": "83 Islands Distillery",
        "url": baseUrl
      },
      "offers": {
        "@type": "Offer",
        "price": eventData.price || "0",
        "priceCurrency": "VUV",
        "availability": "https://schema.org/InStock",
        "url": eventData.bookingUrl || `${baseUrl}/tours`
      }
    }
  }

  // Specific upcoming events (October and November 2025)
  const upcomingSpecialEvents = computed(() => {
    const baseUrl = 'https://83islands.com'
    
    // Last Friday of October 2025 (October 31st)
    const octoberEvent = createEventStructuredData({
      name: "83 Islands Night - October",
      description: "Halloween special edition of 83 Islands Night featuring live music, special rum tastings, and island culture. One Night, 83 Stories.",
      startDate: "2025-10-31T18:00:00+11:00",
      endDate: "2025-10-31T22:00:00+11:00",
      performer: "Musical Guest TBD",
      performerDescription: "Live musical performance featuring local and international artists",
      image: `${baseUrl}/image/83islands_shop.jpg`,
      url: `${baseUrl}/tours`
    })

    // Last Friday of November 2025 (November 28th)
    const novemberEvent = createEventStructuredData({
      name: "83 Islands Night - November",
      description: "November edition of 83 Islands Night featuring live music, special rum tastings, and island culture. One Night, 83 Stories.",
      startDate: "2025-11-28T18:00:00+11:00",
      endDate: "2025-11-28T22:00:00+11:00",
      performer: "Musical Guest TBD",
      performerDescription: "Live musical performance featuring local and international artists",
      image: `${baseUrl}/image/83islands_shop.jpg`,
      url: `${baseUrl}/tours`
    })

    return [octoberEvent, novemberEvent]
  })

  // Function to get tour-specific structured data
  const getTourStructuredData = (selectedDate, tourTimes = []) => {
    const baseUrl = 'https://83islands.com'
    const selectedDateStr = selectedDate ? selectedDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    
    return tourTimes.map((time, index) => {
      const [timeStr, period] = time.split(' ')
      let [hours, minutes] = timeStr.split(':')
      hours = parseInt(hours)
      if (period === 'PM' && hours !== 12) hours += 12
      if (period === 'AM' && hours === 12) hours = 0
      
      const eventDateTime = new Date(selectedDate || new Date())
      eventDateTime.setHours(hours, parseInt(minutes || 0), 0, 0)
      
      return {
        "@context": "https://schema.org",
        "@type": "Event",
        "name": `83 Islands Distillery Tour - ${time}`,
        "description": "Experience the art of rum making at 83 Islands Distillery. Guided tour through our distillery process followed by a curated tasting of our premium rums.",
        "url": `${baseUrl}/tours`,
        "image": [
          `${baseUrl}/image/83islands_location.jpg`,
          `${baseUrl}/image/productionfloor.jpg`,
          `${baseUrl}/image/rackhouse.jpg`
        ],
        "startDate": eventDateTime.toISOString(),
        "endDate": new Date(eventDateTime.getTime() + 60 * 60 * 1000).toISOString(),
        "eventStatus": "https://schema.org/EventScheduled",
        "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
        "location": {
          "@type": "Place",
          "name": "83 Islands Distillery",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Port Vila",
            "addressLocality": "Port Vila",
            "addressRegion": "Shefa Province",
            "postalCode": "PO1276",
            "addressCountry": "VU"
          }
        },
        "organizer": {
          "@type": "Organization",
          "name": "83 Islands Distillery",
          "url": baseUrl
        },
        "offers": [
          {
            "@type": "Offer",
            "name": "Distillery Experience Tour",
            "description": "Guided journey through our artisanal rum-making process",
            "price": "4000",
            "priceCurrency": "VUV",
            "availability": "https://schema.org/InStock",
            "validFrom": selectedDateStr,
            "url": `${baseUrl}/tours`
          },
          {
            "@type": "Offer", 
            "name": "Cellar Door Tasting",
            "description": "Curated tasting of our premium rums",
            "price": "2500",
            "priceCurrency": "VUV",
            "availability": "https://schema.org/InStock",
            "validFrom": selectedDateStr,
            "url": `${baseUrl}/tours`
          }
        ],
        "maximumAttendeeCapacity": 16,
        "remainingAttendeeCapacity": 16
      }
    })
  }

  // Function to generate Article structured data
  const getArticleStructuredData = (article) => {
    if (!article) return null

    const baseUrl = 'https://83islands.com'
    const articleUrl = `${baseUrl}/article/${article.id}`
    
    // Collect all images from the article
    const images = []
    if (article.heroImage) {
      images.push(`${baseUrl}${article.heroImage}`)
    }
    if (article.content) {
      article.content.forEach(section => {
        if (section.image?.url) {
          images.push(`${baseUrl}${section.image.url}`)
        }
        if (section.images) {
          section.images.forEach(img => {
            if (img.url) {
              images.push(`${baseUrl}${img.url}`)
            }
          })
        }
      })
    }
    
    // Build article body text from paragraphs
    const articleBody = article.content
      ? article.content
          .map(section => section.paragraphs?.join(' '))
          .filter(Boolean)
          .join(' ')
      : ''

    return {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.lead || article.title,
      "image": images.length > 0 ? images : [`${baseUrl}${article.heroImage || '/image/83islands_location.jpg'}`],
      "datePublished": article.date || new Date().toISOString(),
      "dateModified": article.date || new Date().toISOString(),
      "author": {
        "@type": "Organization",
        "name": article.author || "83 Islands Distillery",
        "url": baseUrl
      },
      "publisher": {
        "@type": "Organization",
        "name": "83 Islands Distillery",
        "url": baseUrl,
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/image/83IslandsDistillery.svg`
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": articleUrl
      },
      "articleBody": articleBody,
      "articleSection": article.location || "News",
      "keywords": "83 Islands Distillery, Vanuatu rum, distillery, Port Vila, Pacific spirits",
      "inLanguage": "en-US",
      "wordCount": articleBody.split(' ').length
    }
  }

  // Function to inject structured data into page head
  const injectStructuredData = () => {
    // Remove existing structured data
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]')
    existingScripts.forEach(script => script.remove())

    // Add new structured data
    getStructuredData.value.forEach(data => {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.textContent = JSON.stringify(data, null, 2)
      document.head.appendChild(script)
    })
  }

  // Function to inject article structured data
  const injectArticleStructuredData = (article) => {
    // Remove existing structured data
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]')
    existingScripts.forEach(script => script.remove())

    // Get base structured data (organization, breadcrumbs)
    const baseData = [
      organizationData,
      localBusinessData,
      breadcrumbData.value
    ]

    // Add article structured data if article exists
    if (article) {
      const articleData = getArticleStructuredData(article)
      if (articleData) {
        baseData.push(articleData)
      }
    }

    // Inject all structured data
    baseData.forEach(data => {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.textContent = JSON.stringify(data, null, 2)
      document.head.appendChild(script)
    })
  }

  return {
    organizationData,
    localBusinessData,
    tourEventsData,
    islandsNightsEvents,
    upcomingSpecialEvents,
    productData,
    breadcrumbData,
    getStructuredData,
    getTourStructuredData,
    createEventStructuredData,
    getArticleStructuredData,
    injectStructuredData,
    injectArticleStructuredData
  }
}
